import {ComponentProps, useEffect, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import {ApplicantsScreeningRow} from '../ApplicantsScreeningRow/ApplicantsScreeningRow';
import {activeScreeningApplicantCount} from '../components/ScreeningStatusCountDisplay';
import {ScreeningApplicant, ScreeningTotalCountPerStatus} from '../types/screeningExternalApplicantsTypes';

import {BasicTable} from '../../../../components/BasicTable/BasicTable';
import {createBasicTableColumn} from '../../../../components/BasicTable/createBasicTableColumn';
import {TablePaginator} from '../../../../components/TablePaginator/TablePaginator';
import {AppConfig} from '../../../../lib/AppConfig';

interface Props {
  page: number;
  onPageChange: (page: number) => void;
  screeningApplicants: ScreeningApplicant[];
  totalCountPerStatus?: ScreeningTotalCountPerStatus;
  pagination?: {
    page: number;
    perPage: number;
    totalPages: number;
    totalCount: number;
    nextPage: number | null;
    previousPage: number | null;
  };
  hasNext?: boolean;
  getNext?: () => void;
  refetch: () => void;
  isLoading: boolean;
}

type RowProps = ComponentProps<typeof ApplicantsScreeningRow>;

export const ApplicantsScreeningTable = ({
  page,
  onPageChange,
  screeningApplicants,
  totalCountPerStatus,
  pagination,
  hasNext = false,
  getNext,
  refetch,
  isLoading,
}: Props) => {
  const {t} = useTranslation('translation', {keyPrefix: 'screeningModule.applicantScreeningColumns'});
  const itemsPerPage = AppConfig.PAGINATION_PER_PAGE;
  // Guard against inconsistent backend metadata (e.g. totalPages > 1 but totalCount == perPage).
  const minCountFromTotalPages =
    pagination && pagination.totalPages > 1 ? (pagination.totalPages - 1) * itemsPerPage + 1 : 0;
  // Deleted applicants are tracked in stats but should not inflate the active list pagination total.
  const activeCountFromStatuses = totalCountPerStatus ? activeScreeningApplicantCount(totalCountPerStatus) : 0;
  const totalItems = Math.max(
    activeCountFromStatuses,
    pagination?.totalCount ?? 0,
    minCountFromTotalPages,
    screeningApplicants.length
  );
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const safePage = Math.min(page, totalPages);

  useEffect(() => {
    const requiredItems = safePage * itemsPerPage;
    const reportedTotal = pagination?.totalCount;
    const mayHaveMoreOnServer =
      !!hasNext ||
      !!pagination?.nextPage ||
      (pagination?.totalPages ? screeningApplicants.length < pagination.totalPages * itemsPerPage : false) ||
      (reportedTotal !== undefined ? screeningApplicants.length < reportedTotal : false);
    if (screeningApplicants.length < requiredItems && mayHaveMoreOnServer && hasNext && getNext) {
      getNext();
    }
  }, [
    safePage,
    itemsPerPage,
    screeningApplicants.length,
    hasNext,
    getNext,
    pagination?.nextPage,
    pagination?.totalCount,
    pagination?.totalPages,
  ]);

  const pagedApplicants = useMemo(() => {
    const start = (safePage - 1) * itemsPerPage;
    return screeningApplicants.slice(start, start + itemsPerPage);
  }, [screeningApplicants, safePage, itemsPerPage]);

  const columns = useMemo(
    () => [
      createBasicTableColumn('name', t('name'), {sx: {width: '180px'}}),
      createBasicTableColumn('job-posting', t('jobPosting'), {sx: {width: '260px'}}),
      createBasicTableColumn('location', t('location'), {minWidth: '150px'}),
      createBasicTableColumn('status', t('status'), {minWidth: '160px'}),
      createBasicTableColumn('last-updated', t('lastUpdated'), {minWidth: '144px'}),
      createBasicTableColumn<RowProps>('culture-fit', t('fit'), {
        minWidth: '70px',
        sortable: true,
        getSortValue: (row) => row.talentSurvey?.culturalFit ?? null,
      }),
      createBasicTableColumn('hamburger-menu', '', {sx: {width: '140px'}}),
    ],
    [t]
  );

  const rowData = useMemo(
    () =>
      pagedApplicants.map((applicant: ScreeningApplicant) => ({
        ...applicant,
        id: applicant.resourceId,
        refetch,
      })),
    [pagedApplicants, refetch]
  );

  return (
    <>
      <BasicTable<ComponentProps<typeof ApplicantsScreeningRow>>
        columns={columns}
        Row={ApplicantsScreeningRow}
        rowData={rowData}
        isLoading={isLoading}
      />
      <TablePaginator
        page={safePage}
        totalItems={totalItems}
        itemsPerPage={itemsPerPage}
        translationKeyPrefix='screeningModule.pagination'
        onPageChange={onPageChange}
      />
    </>
  );
};
